import React, { useMemo, useState } from "react";
import type { MajorArcanaCard } from "../data/majorArcana";

type ArcanaLibraryProps = {
  cards: MajorArcanaCard[];
};

export function ArcanaLibrary({ cards }: ArcanaLibraryProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cards;

    return cards.filter((c) => {
      const hay = `${c.name} ${c.keywords.join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [cards, query]);

  return (
    <>
      {/* Desktop: Sticky sidebar */}
      <div className="hidden lg:block bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white lg:sticky lg:top-6 max-h-[calc(100vh-120px)] overflow-hidden flex-col">
        <div className="mb-4">
          <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
            <svg className="w-5 h-5 text-joy-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
            Major Arcana
          </h2>
          
          <input
            className="w-full px-4 py-2.5 bg-joy-50 border-2 border-joy-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-joy-500 focus:border-joy-500 text-sm text-slate-700 placeholder-slate-400 transition-all"
            type="text"
            placeholder="Search cards..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="flex-1 overflow-y-auto pr-2 space-y-2 scrollbar-thin scrollbar-thumb-joy-300 scrollbar-track-transparent">
          {filtered.length === 0 && (
            <div className="text-center py-8 text-slate-400 text-sm italic">
              No matches. Try "sun" or "moon"
            </div>
          )}

          {filtered.map((card) => (
            <a
              key={card.id}
              className="block p-4 rounded-xl bg-gradient-to-br from-white to-joy-50/30 border-2 border-joy-100 hover:border-joy-400 hover:shadow-md hover:-translate-y-1 transition-all duration-300 group"
              href={card.articleUrl}
              target="_blank"
              rel="noreferrer"
              title={`Learn about ${card.name}`}
            >
              <div className="font-bold text-slate-800 group-hover:text-joy-700 mb-1.5 text-base">
                {card.name}
              </div>
              <div className="text-xs text-slate-500 leading-relaxed mb-2">
                {card.shortMeaning}
              </div>
              <div className="flex flex-wrap gap-1">
                {card.keywords.slice(0, 2).map((kw) => (
                  <span key={kw} className="px-2 py-0.5 bg-joy-100 text-joy-700 text-xs font-medium rounded">
                    {kw}
                  </span>
                ))}
              </div>
            </a>
          ))}
        </div>
        
        <div className="mt-4 pt-4 border-t border-joy-200">
          <div className="text-xs text-slate-500 text-center">
            {filtered.length} of {cards.length} cards
          </div>
        </div>
      </div>

      {/* Mobile: Bottom sheet toggle */}
      <div className="lg:hidden">
        <button
          onClick={() => setOpen(!open)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-br from-joy-500 to-purple-600 rounded-full shadow-2xl flex items-center justify-center text-white hover:scale-110 transition-transform duration-300"
          aria-label="Toggle card library"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        </button>

        {/* Bottom sheet overlay */}
        {open && (
          <>
            <div 
              className="fixed inset-0 bg-black/50 z-40 animate-fade-in"
              onClick={() => setOpen(false)}
            />
            <div className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl max-h-[80vh] flex flex-col animate-slide-up">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <svg className="w-5 h-5 text-joy-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                  Major Arcana Library
                </h2>
                <button 
                  onClick={() => setOpen(false)}
                  className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              
              <div className="p-6 pb-8 overflow-y-auto flex-1">
                <input
                  className="w-full px-4 py-3 bg-joy-50 border-2 border-joy-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-joy-500 focus:border-joy-500 text-sm text-slate-700 placeholder-slate-400 transition-all mb-4"
                  type="text"
                  placeholder="Search cards..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                
                <div className="space-y-3">
                  {filtered.length === 0 && (
                    <div className="text-center py-8 text-slate-400 text-sm italic">
                      No matches. Try "sun" or "moon"
                    </div>
                  )}

                  {filtered.map((card) => (
                    <a
                      key={card.id}
                      className="block p-4 rounded-xl bg-gradient-to-br from-white to-joy-50/30 border-2 border-joy-100 active:scale-95 transition-all"
                      href={card.articleUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => setOpen(false)}
                    >
                      <div className="font-bold text-slate-800 mb-1.5 text-base">
                        {card.name}
                      </div>
                      <div className="text-xs text-slate-500 leading-relaxed mb-2">
                        {card.shortMeaning}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {card.keywords.slice(0, 2).map((kw) => (
                          <span key={kw} className="px-2 py-0.5 bg-joy-100 text-joy-700 text-xs font-medium rounded">
                            {kw}
                          </span>
                        ))}
                      </div>
                    </a>
                  ))}
                </div>
                
                <div className="mt-6 pt-4 border-t border-joy-200">
                  <div className="text-xs text-slate-500 text-center">
                    {filtered.length} of {cards.length} cards
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
